import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--space-selector-form"
//
// Every space group's branches are rendered up front (one <fieldset> per group,
// each using Ui::CheckboxComponent). This controller:
//   - shows only the selected group's branches and disables the other groups'
//     inputs so they are never submitted;
//   - keeps each group's "All branches" toggle in sync with its branches;
//   - mirrors selection onto each row (border + label colour) so only the
//     checked row shows the corporate/primary colour.
export default class extends Controller {
  static targets = ["groupBranches"]

  connect() {
    this.groupSelect = this.element.querySelector(
      'select[name="selected_space_group_key"]',
    )
    if (this.groupSelect) {
      this._onGroupChange = this.groupChanged.bind(this)
      // Tom Select fires native change events on the underlying <select>.
      this.groupSelect.addEventListener("change", this._onGroupChange)
      this.showGroup(this.groupSelect.value)
    }

    // Reflect the server-rendered checked state onto each row's styling.
    this.element
      .querySelectorAll('input[type="checkbox"]')
      .forEach((cb) => this.updateCheckboxStyle(cb))
  }

  disconnect() {
    if (this.groupSelect && this._onGroupChange) {
      this.groupSelect.removeEventListener("change", this._onGroupChange)
    }
  }

  groupChanged(event) {
    this.showGroup(event.target.value)
  }

  // Show/enable only the selected group's branches; hide + disable the rest so
  // hidden groups never submit their checkboxes.
  showGroup(groupKey) {
    this.groupBranchesTargets.forEach((group) => {
      const active = group.dataset.groupKey === groupKey
      group.classList.toggle("hidden", !active)
      group.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.disabled = !active
      })
    })
  }

  updateSelection(event) {
    const checkbox = event.target
    this.updateCheckboxStyle(checkbox)

    if (checkbox.name === "selected_branch_keys[]") {
      this.syncAllBranches(checkbox)
    }
  }

  toggleAllBranches(event) {
    const allCheckbox = event.target
    const group = allCheckbox.closest(
      '[data-ui--space-selector-form-target="groupBranches"]',
    )
    if (!group) return

    const checked = allCheckbox.checked
    group
      .querySelectorAll('input[name="selected_branch_keys[]"]')
      .forEach((cb) => {
        cb.checked = checked
        this.updateCheckboxStyle(cb)
      })
    this.updateCheckboxStyle(allCheckbox)
  }

  // Keep a group's "All branches" toggle in sync with its branch checkboxes.
  syncAllBranches(branchCheckbox) {
    const group = branchCheckbox.closest(
      '[data-ui--space-selector-form-target="groupBranches"]',
    )
    if (!group) return

    const all = group.querySelector('input[name="all_branches"]')
    if (!all) return

    const branches = group.querySelectorAll(
      'input[name="selected_branch_keys[]"]',
    )
    all.checked =
      branches.length > 0 && Array.from(branches).every((cb) => cb.checked)
    this.updateCheckboxStyle(all)
  }

  // Mirror the checkbox state onto its row: primary border + primary label text
  // when checked, neutral otherwise. Only the selected row is highlighted.
  updateCheckboxStyle(checkbox) {
    const card = checkbox.closest("[data-branch-card]")
    if (!card) return

    const label = card.querySelector("label")
    if (checkbox.checked) {
      card.classList.remove("border-border", "hover:border-foreground-medium")
      card.classList.add("border-primary")
      if (label) {
        label.classList.remove("text-foreground-medium")
        label.classList.add("text-primary")
      }
    } else {
      card.classList.remove("border-primary")
      card.classList.add("border-border", "hover:border-foreground-medium")
      if (label) {
        label.classList.remove("text-primary")
        label.classList.add("text-foreground-medium")
      }
    }
  }
}
